import type { OrderIntent, ResolutionEnumNoPrefix, SideEnum } from '@talos/kyoko';
import {
  EMPTY_ARRAY,
  isMultileg,
  UnifiedLiquidityEnum,
  useHistoricalPrices,
  useSecuritiesContext,
  VolumeLadderContent,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useState } from 'react';
import { useMarketDataSnapshot } from '../../../../../hooks';
import { usePrimeFromOrderBook } from '../../../../../hooks/usePrimeFromOrderBook';
import { MULTILEG_MARKET_ACCOUNT } from '../../../../../providers/OMSContext.types';
import type { SingleSymbolPanelConfig, VolumeLadderPanelConfig } from '../../../../../types/LayoutConfig';
import { Panel } from '../../../../Layout/Components/Panel';
import { SingleSymbolPanelHeader } from './SingleSymbolPanelHeader';
import { SingleSymbolPanelSettings } from './SingleSymbolPanelSettings';
import { SizeBucketSettings } from './SizeBucketSettings';
import { SparklineResolutionSetting } from './SparklineResolutionSetting';

export function VolumeLadderPanel({ api, params }: IDockviewPanelProps<VolumeLadderPanelConfig>) {
  const {
    aggregation,
    symbol,
    marketAccounts,
    markets,
    sizeBuckets,
    resolution,
    enableUnifiedLiquidity,
    unifiedLiquidityMarketSymbols,
    settingsInitialOpen,
    orderPresetID,
    subAccount,
  } = params;
  const { securitiesBySymbol, searchableSecurities } = useSecuritiesContext();
  const [showSettings, setShowSettings] = useState(false);

  const security = symbol ? securitiesBySymbol.get(symbol) : undefined;

  // Open settings panel when the panel is first added and `settingsInitialOpen` is true
  useEffect(() => {
    if (settingsInitialOpen) {
      setShowSettings(true);
      api.updateParameters({ settingsInitialOpen: false });
    }
  }, [settingsInitialOpen, api]);

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (symbol) {
        const security = securitiesBySymbol.get(symbol);
        api.updateParameters({
          symbol,
          sizeBuckets: security?.SizeBuckets ?? EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          marketAccounts: isMultileg(security) ? [MULTILEG_MARKET_ACCOUNT] : EMPTY_ARRAY,
          aggregation: undefined,
          enableUnifiedLiquidity: false,
          unifiedLiquidityMarketSymbols: [],
        });
        api.setTitle(security ? `${security?.DisplaySymbol} volumes` : 'Volume Ladder');
      }
    },
    [api, securitiesBySymbol]
  );

  const { marketDataSnapshots: marketDataObservable } = useMarketDataSnapshot({
    symbol,
    sizeBuckets: sizeBuckets ?? security?.SizeBuckets,
    marketAccounts,
    markets,
    currency: security?.BaseCurrency,
    priceIncrement: security?.MinPriceIncrement,
    tag: `VolumeLadderPanel_${api.id}`,
    includeTradeDirection: false,
    unifiedLiquidity: enableUnifiedLiquidity ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
    unifiedLiquidityMarketSymbols,
  });

  const { marketDataSnapshots: marketViewObservable } = useMarketDataSnapshot({
    symbol,
    marketAccounts,
    markets,
    currency: security?.BaseCurrency,
    priceIncrement: security?.MinPriceIncrement,
    depth: 1,
    tag: `VolumeLadderPanel_${api.id}`,
    includeTradeDirection: false,
    unifiedLiquidity: enableUnifiedLiquidity ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
    unifiedLiquidityMarketSymbols,
  });

  const sparklineDataObservable = useHistoricalPrices({ symbol, resolution });

  const primeFromOrderBook = usePrimeFromOrderBook({
    security,
    marketAccounts: marketAccounts,
    unifiedLiquidityMarketSymbols,
    subAccount: subAccount,
    orderPresetID,
    aggregation,
  });
  const handleClickRow = useCallback(
    (row: { side: SideEnum; price: string; size?: string; intent?: OrderIntent }) => {
      primeFromOrderBook({
        side: row.side,
        price: row.price,
        intent: row.intent,
        quantity: row.size,
      });
    },
    [primeFromOrderBook]
  );

  const handleUpdateParameters = useCallback(
    (update: Partial<SingleSymbolPanelConfig>) => {
      api.updateParameters(update);
    },
    [api]
  );

  const handleSizeBucketsChange = useCallback(
    (sizeBuckets: string[]) => {
      api.updateParameters({ sizeBuckets });
    },
    [api]
  );

  const handleResolutionChange = useCallback(
    (resolution: ResolutionEnumNoPrefix) => {
      api.updateParameters({ resolution });
    },
    [api]
  );

  return (
    <>
      <Panel
        panelApi={api}
        display="flex"
        flexDirection="column"
        background="backgroundContent"
        h="100%"
        data-testid="volume-ladder-panel"
      >
        <SingleSymbolPanelHeader
          security={security}
          aggregation={aggregation}
          markets={markets}
          marketAccounts={marketAccounts}
          enableUnifiedLiquidity={enableUnifiedLiquidity}
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          panelTypeLabel="Volume Ladder"
        />
        {showSettings ? (
          <SingleSymbolPanelSettings
            onClose={() => setShowSettings(false)}
            onSymbolChange={handleSymbolChange}
            onUpdateParameters={handleUpdateParameters}
            orderPresetID={orderPresetID}
            subAccount={subAccount}
            marketAccounts={marketAccounts}
            markets={markets}
            aggregation={aggregation}
            symbol={symbol}
            availableSecurities={searchableSecurities}
            allowMultileg={true}
            allowUnifiedLiquidity={true}
            enableUnifiedLiquidity={enableUnifiedLiquidity}
            unifiedLiquidityMarketSymbols={unifiedLiquidityMarketSymbols}
            customSettings={
              <>
                <SparklineResolutionSetting onChange={handleResolutionChange} value={resolution ?? '1H'} />
                <SizeBucketSettings onChange={handleSizeBucketsChange} value={sizeBuckets ?? security?.SizeBuckets} />
              </>
            }
          />
        ) : (
          <VolumeLadderContent
            marketDataObservable={marketDataObservable}
            marketViewObservable={marketViewObservable}
            sizeBuckets={sizeBuckets ?? security?.SizeBuckets}
            resolution={resolution}
            sparklineDataObservable={sparklineDataObservable}
            showSpread={true}
            security={security}
            usePreciseTopOfBookIncrement={false}
            onClickRow={handleClickRow}
            size={{
              offsetWidth: api?.width,
              offsetHeight: api?.height,
            }}
          />
        )}
      </Panel>
    </>
  );
}
